import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudOff, Loader2, RotateCw } from 'lucide-react';
import { useAppConfig } from '@shared/app/app-config';
import { retrySessionRestore } from '@shared/auth/restore-session';
import { PATHS } from '@shared/router/paths';

/**
 * Shown when the session could not be restored because identity-service did
 * not answer (unreachable, 5xx, 429) — opening this application from the
 * switcher, or reloading it. The session itself may still be valid, so the
 * first offer is to try again; signing in from scratch would only start a
 * second session beside it.
 */
export function SessionUnavailablePage() {
  const { name } = useAppConfig();
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      await retrySessionRestore();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-warning/15 text-warning">
          <CloudOff className="size-6" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">Your session could not be restored</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {name} could not reach the sign-in service. Your session may still be valid — try again in
          a moment.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => void retry()}
            disabled={retrying}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {retrying ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <RotateCw className="size-4" aria-hidden="true" />
            )}
            Try again
          </button>
          <Link to={PATHS.auth.login} className="text-sm font-medium text-primary hover:underline">
            Sign in again
          </Link>
        </div>
      </div>
    </div>
  );
}
